import { cn } from "@/lib/cn";
import {
  CONFIDENCE_META,
  CONFIDENCE_THRESHOLDS as SHARED_THRESHOLDS,
  type ConfidenceTier,
  tierForConfidence as sharedTierForConfidence,
  clampConfidence,
  confidencePct,
} from "@/lib/ai-supervision";

/**
 * Canonical AI-confidence indicator.
 *
 * Presentational shell — bar + percentage + qualitative line —
 * around the shared semantics in `@/lib/ai-supervision`. Every
 * surface that shows an AI confidence number reads the SAME
 * thresholds and color tokens, so an operator who learns "yellow
 * means double-check" once gets that meaning everywhere.
 *
 *   ≥ 0.85 → high   — operator can trust the suggestion outright
 *   ≥ 0.70 → medium — worth a glance before approving
 *   <0.70  → low    — review the input before acting
 *
 * Pages decide what to do with the confidence (auto-apply on high,
 * prompt on medium, hold on low, etc.) — `<RiskBadge>` covers the
 * action-side of that decision.
 *
 * For surfaces that need an operator-guidance sentence ("Safe to
 * review quickly" etc.) use `<ConfidenceSummary>` from
 * `@/components/ai-supervision` — that primitive embeds this
 * indicator plus the canonical guidance copy.
 */

// Re-export the shared constants under their historical names so
// existing callsites that imported `CONFIDENCE_THRESHOLDS` /
// `ConfidenceTier` / `tierForConfidence` from this module keep
// working unchanged.
export const CONFIDENCE_THRESHOLDS = SHARED_THRESHOLDS;
export type { ConfidenceTier };
export const tierForConfidence = sharedTierForConfidence;

export type ConfidenceIndicatorProps = {
  /** 0..1; clamped silently. NaN renders as 0 (low tier). */
  value: number;
  /**
   * `md` (default) — bar + percentage + qualitative line below
   *   (matches the existing Issues AI Analysis treatment).
   * `sm` — bar + percentage inline, no qualitative line. Used in
   *   tight side-pane facts where the row already labels the field.
   */
  size?: "sm" | "md";
  /** Optional override for the qualitative line — pass `t("…High")`
   *  when the consumer is i18n-aware. */
  highLabel?: string;
  mediumLabel?: string;
  lowLabel?: string;
  /** Skip the qualitative line entirely (default `md` keeps it). */
  hideLabel?: boolean;
  className?: string;
};

export function ConfidenceIndicator({
  value,
  size = "md",
  highLabel = "High confidence",
  mediumLabel = "Medium confidence",
  lowLabel = "Low confidence",
  hideLabel = false,
  className,
}: ConfidenceIndicatorProps) {
  const v = clampConfidence(value);
  const pct = confidencePct(v);
  const tier = tierForConfidence(v);
  const meta = CONFIDENCE_META[tier];
  const label =
    tier === "high"   ? highLabel :
    tier === "medium" ? mediumLabel :
                        lowLabel;
  return (
    <div className={cn("min-w-0", className)}>
      <div className="flex items-center gap-2.5">
        <div className="h-1.5 flex-1 rounded-full bg-bg-hover overflow-hidden" aria-hidden>
          <div
            className={cn("h-full rounded-full", meta.barCls)}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span
          className={cn(
            "tabular-nums font-semibold",
            size === "md" ? "text-[13px] text-fg" : "text-[11.5px]",
            size === "sm" && meta.textCls,
          )}
          aria-label={`${pct}% confidence (${tier})`}
        >
          {pct}%
        </span>
      </div>
      {size === "md" && !hideLabel && (
        <div className="text-[11.5px] text-fg-muted mt-1.5">{label}</div>
      )}
    </div>
  );
}
